import type { Job, Timesheet } from '@rendimees24/shared-types';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocFromCache,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

import { forgetCachedContact } from '../hooks/useJobContact';

import { getFirebase } from './firebase';
import { startWorkDayQueued } from './syncQueue';

/**
 * Comparable millis for a createdAt that may be an ISO string OR a Firestore
 * Timestamp (serverTimestamp() reads back as a Timestamp, not a string — so a
 * naive string sort throws "localeCompare is not a function").
 */
function createdAtMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return Date.parse(v) || 0;
  const t = v as { toMillis?: () => number; seconds?: number };
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  return 0;
}

/**
 * Subscribe to jobs the worker is currently assigned to. The Firestore rule
 * requires `uid in resource.data.assignments`, which assumes assignments is
 * a flat array of worker UIDs — different from `Job.assignments` (an array
 * of objects). Worker views read whatever the query returns; the rule
 * mismatch will be unified once EPIC 8 finalises the model.
 */
export function subscribeWorkerJobs(
  uid: string,
  onSnap: (jobs: Job[], meta: { fromCache: boolean }) => void,
  onError?: (e: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  // Slice 8.1 — uses the denormalised `assignedWorkerIds[]` flat UID array
  // populated by `assignWorker` / `unassignWorker`. Matches firestore.rules.
  // No server-side orderBy: `array-contains` + `orderBy` on another field needs
  // a composite index; worker job lists are tiny, so we sort client-side and
  // avoid the index dependency entirely.
  const q = query(collection(firestore, 'jobs'), where('assignedWorkerIds', 'array-contains', uid));
  return onSnapshot(
    q,
    (snap: QuerySnapshot) => {
      const jobs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Job);
      jobs.sort((a, b) =>
        (a.requirements?.startAt ?? '').localeCompare(b.requirements?.startAt ?? ''),
      );
      onSnap(jobs, { fromCache: snap.metadata?.fromCache ?? false });
    },
    (err) => onError?.(err as Error),
  );
}

export async function fetchJob(jobId: string): Promise<Job | null> {
  const { firestore } = getFirebase();
  const snap = await getDoc(doc(firestore, 'jobs', jobId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Job;
}

/**
 * UX 1C.3 — the worker's job view must open offline once it was opened
 * online (the full arrival guide and contact card live there). Same approach
 * as the 1A contact copy (`hooks/useJobContact.ts`): the last server answer
 * is kept on the device, per user, and served when the network is not there.
 *
 * The device copy is the witness that the server last let this worker read
 * the job: without it nothing is served offline — not even Firestore's own
 * IndexedDB cache, which keeps a document after access ends. With it,
 * Firestore's cache (fresher) is preferred, then the copy.
 *
 * Online: the server; a network failure falls back the same way. A server
 * answer "no such job" or "no access" (permission-denied) is NOT papered over:
 * the job copy AND the 1A contact/arrival-guide copy are dropped and the
 * answer stands, so a removed assignment does not linger offline.
 * `pruneWorkerJobCopies` also drops copies of jobs no longer in the worker's
 * live job list.
 */
const workerJobKey = (uid: string, jobId: string) => `rm24.worker.job.${uid}.${jobId}`;

export interface CachedWorkerJob {
  job: Job;
  /** ISO instant of the server answer. */
  at: string;
}

export function readCachedWorkerJob(uid: string, jobId: string): CachedWorkerJob | null {
  try {
    const raw = window.localStorage.getItem(workerJobKey(uid, jobId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedWorkerJob;
    return parsed?.job && typeof parsed.at === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedWorkerJob(uid: string, jobId: string, job: Job | null): void {
  try {
    if (job) {
      const value: CachedWorkerJob = { job, at: new Date().toISOString() };
      window.localStorage.setItem(workerJobKey(uid, jobId), JSON.stringify(value));
    } else {
      window.localStorage.removeItem(workerJobKey(uid, jobId));
    }
  } catch {
    /* quota / private mode — the live answer is still shown */
  }
}

const OFFLINE_CODES = new Set(['unavailable', 'deadline-exceeded']);

function isOfflineError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code ?? '';
  return (
    OFFLINE_CODES.has(code.replace(/^firestore\//, '')) ||
    /offline/i.test(String((err as Error)?.message ?? ''))
  );
}

const NO_ACCESS_CODES = new Set(['permission-denied', 'not-found']);

function isNoAccessError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code ?? '';
  return NO_ACCESS_CODES.has(code.replace(/^firestore\//, ''));
}

/** Forget everything the device holds for one job (job copy + contact/guide). */
function forgetWorkerJob(uid: string, jobId: string): void {
  writeCachedWorkerJob(uid, jobId, null);
  forgetCachedContact(jobId);
}

/**
 * Drop the device copies of jobs that are no longer in the worker's live job
 * list. Call ONLY with a server answer (not a cache snapshot) — an offline
 * snapshot may be partial.
 */
export function pruneWorkerJobCopies(uid: string, liveJobIds: readonly string[]): void {
  const prefix = `rm24.worker.job.${uid}.`;
  const live = new Set(liveJobIds);
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(prefix) && !live.has(key.slice(prefix.length))) {
        stale.push(key.slice(prefix.length));
      }
    }
    for (const jobId of stale) forgetWorkerJob(uid, jobId);
  } catch {
    /* storage unavailable */
  }
}

async function fromDeviceCopy(uid: string, jobId: string): Promise<Job | null> {
  const copy = readCachedWorkerJob(uid, jobId);
  // No witness that the server still lets this worker read the job.
  if (!copy) return null;
  const { firestore } = getFirebase();
  try {
    const snap = await getDocFromCache(doc(firestore, 'jobs', jobId));
    if (snap.exists()) return { id: snap.id, ...snap.data() } as Job;
  } catch {
    /* not in Firestore's cache (or no IndexedDB) — try the device copy */
  }
  return copy.job;
}

export async function fetchWorkerJob(jobId: string, uid: string): Promise<Job | null> {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline) {
    const cached = await fromDeviceCopy(uid, jobId);
    if (cached) return cached;
  }
  try {
    const job = await fetchJob(jobId);
    if (job) writeCachedWorkerJob(uid, jobId, job);
    else forgetWorkerJob(uid, jobId);
    return job;
  } catch (err) {
    if (isNoAccessError(err)) {
      forgetWorkerJob(uid, jobId);
      throw err;
    }
    if (offline || isOfflineError(err)) {
      const cached = await fromDeviceCopy(uid, jobId);
      if (cached) return cached;
    }
    throw err;
  }
}

/**
 * Subscribe to all timesheets where the caller is the worker. Uses a
 * collectionGroup query against `timesheets` (sub-collection under jobs).
 * No server orderBy (collectionGroup ordering needs a collection-group index);
 * sorted client-side. Has an error callback so a transient permission error
 * during auth transitions doesn't throw an uncaught snapshot error.
 */
export function subscribeMyTimesheets(
  uid: string,
  onSnap: (timesheets: Timesheet[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  const q = query(collectionGroup(firestore, 'timesheets'), where('workerId', '==', uid));
  return onSnapshot(
    q,
    (snap: QuerySnapshot) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Timesheet);
      list.sort((a, b) => createdAtMillis(b.createdAt) - createdAtMillis(a.createdAt));
      onSnap(list);
    },
    (err) => onError?.(err as Error),
  );
}

/**
 * Subscribe to ONE job's timesheets for this worker — a plain subcollection
 * query (`jobs/{jobId}/timesheets where workerId == uid`). This is the
 * canonical secured-query pattern: the where clause matches the Firestore read
 * rule exactly, so unlike the cross-job collectionGroup query it can't be
 * rejected wholesale, and it needs no composite index. Used by the worker job
 * page so check-in / break / photos / completion always see the live draft.
 */
export function subscribeJobTimesheets(
  jobId: string,
  uid: string,
  onSnap: (timesheets: Timesheet[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  const q = query(collection(firestore, 'jobs', jobId, 'timesheets'), where('workerId', '==', uid));
  return onSnapshot(
    q,
    (snap: QuerySnapshot) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Timesheet);
      list.sort((a, b) => createdAtMillis(b.createdAt) - createdAtMillis(a.createdAt));
      onSnap(list);
    },
    (err) => onError?.(err as Error),
  );
}

export interface CheckInPayload {
  continueTimesheetId?: string;
  startedAt?: string;
  jobId: string;
  workerId: string;
  clientId: string;
  /**
   * Device coordinates at the tap. G1 (PB-18): the server judges from these
   * whether the start was on site; the device sends no verdict of its own.
   */
  location?: { latitude: number; longitude: number };
}

/**
 * Start a working day.
 *
 * This was a direct Firestore write, with the "one open day at a time" rule
 * enforced by a query this same function ran first — scoped to one job,
 * because that was the collection it had. Two devices could both read no
 * open day and both write one; a worker who forgot to finish on object A
 * could start on object B by design. Neither is fixable from here: a client
 * cannot serialise against another client.
 *
 * It now goes through `startWorkDay`, which decides inside a transaction on
 * a single per-worker marker. The result says whether the day actually
 * exists on the server or is still a note on this device — see
 * `startWorkDayQueued`; the two must not be shown alike.
 */
export async function checkIn(
  p: CheckInPayload,
): Promise<{ queued: boolean; timesheetId?: string; localRef: string }> {
  return startWorkDayQueued({
    continueTimesheetId: p.continueTimesheetId,
    jobId: p.jobId,
    startedAt: p.startedAt,
    location: p.location,
  });
}

// EPIC 39 — the client-side checkOut write was replaced by the
// `completeWorkDay` callable (services/timesheets.ts): the server stamps the
// end time, applies the statutory auto-lunch and flips the sheet to
// 'submitted'. firestore.rules no longer permit direct worker writes to
// entries / totalHours.

/**
 * Best-effort browser geolocation; resolves to undefined if denied or
 * unsupported, so callers can still check in without GPS.
 */
export async function getBrowserLocation(): Promise<
  { latitude: number; longitude: number } | undefined
> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return undefined;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(undefined),
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 60_000 },
    );
  });
}
